import { CloseIcon } from 'assets/icons/CloseIcon'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import { createBatch } from 'features/fuelManagement/fuelManagementSlice'
import { getFuelCustodyConfig } from 'features/fuelManagement/fuelCustodySlice'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { AppDispatch, RootState } from 'store'
import { Button, CursorPointer, Flex } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { fuelTypeSelect, fuelTypes, parseFuelTankOptions } from './fuelData'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { formatNumbersForDisplay } from 'utils/helpers'
import { BatchFieldsGrid, BatchModalBody, BatchModalFooter } from './AddBatchModal.styled'

const DEFAULT_DELIVERY_TOLERANCE_PCT = 0.5

const TEXT_FIELDS = ['supplier', 'waybillRef', 'sealNumbers']

export const AddBatchModal = ({ closeModal }: { closeModal: any }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { config } = useSelector((store: RootState) => store.fuelCustody)
  const [state, setState] = useState<IFuelBatchModal>({
    fuelTank: null,
    fuelType: null,
    amount: 0,
    price: 0,
    supplier: null,
    date: new Date(),
    waybillRef: '',
    waybillLitres: 0,
    tankDipBefore: 0,
    tankDipAfter: 0,
    sealNumbers: '',
  })

  useEffect(() => {
    dispatch(getFuelCustodyConfig() as any)
  }, [dispatch])

  const deliveryTolerancePct = config?.deliveryTolerancePct ?? DEFAULT_DELIVERY_TOLERANCE_PCT

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, valueAsNumber, value } = e.target
    setState((prev) => {
      const copy = { ...prev }
      copy[name] = TEXT_FIELDS.includes(name) ? value : valueAsNumber
      return copy
    })
  }

  const handleSave = async () => {
    if (!state.amount || !state.price || !state.supplier || !state.fuelTank || !state.fuelType) {
      toast.warn('All fields are required.')
      return
    }
    const tankCap = (state.fuelTank as any)?.capacity
    const tankLevel = (state.fuelTank as any)?.currentAmount
    const credited =
      state.tankDipAfter && state.tankDipBefore
        ? state.tankDipAfter - state.tankDipBefore
        : state.amount
    if (
      tankCap != null &&
      tankLevel != null &&
      Number(tankLevel) + Number(credited) > Number(tankCap)
    ) {
      toast.warn(
        `Delivery would exceed tank capacity (${tankCap}L). Current level: ${tankLevel}L, delivering: ${credited}L.`,
      )
      return
    }
    await dispatch(createBatch(state))
    closeModal()
  }

  const handleDate = (date: Date, name: string) => {
    setState((prev) => {
      const copy = { ...prev }
      copy[name] = date
      return copy
    })
  }

  const parse = (data: any) => {
    return data.data.map((item) => ({ label: item.name, value: item.id }))
  }

  const handleSelect = (value: ISelectValue, name: string) => {
    setState((prev) => {
      const copy = { ...prev }
      copy[name] = value
      if (name === 'fuelTank' && (value as any)?.fuelType != null) {
        copy.fuelType = fuelTypeSelect((value as any).fuelType)
      }
      return copy
    })
  }

  const measuredIn =
    state.tankDipAfter && state.tankDipBefore ? state.tankDipAfter - state.tankDipBefore : null
  const variance =
    measuredIn != null && state.waybillLitres ? measuredIn - state.waybillLitres : null
  const variancePct =
    variance != null && state.waybillLitres
      ? Math.round((variance / state.waybillLitres) * 1000) / 10
      : null
  const withinTolerance = variancePct != null && Math.abs(variancePct) <= deliveryTolerancePct

  return (
    <Modal black width='640px'>
      <BatchModalBody>
        <Flex between width='100%' center>
          <TitleText weight='bold'>Add a batch</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <BatchFieldsGrid>
          <Input
            placeholder='Enter amount'
            labelText='Amount'
            value={state.amount}
            onChange={handleChange}
            type='number'
            name='amount'
            nomarg
          />
          <Input
            placeholder='Enter price'
            labelText='Price'
            value={state.price}
            onChange={handleChange}
            type='number'
            name='price'
            nomarg
          />
          <HonestSelectAsyncInput
            link='fuel/management/tank'
            name='fuelTank'
            labelText='Fuel Tank'
            onChange={handleSelect}
            parserFunction={parseFuelTankOptions}
            nomarg
          />
          <HonestSelectAsyncInput
            link='fuel/management/supplier'
            name='supplier'
            labelText='Fuel Supplier'
            onChange={handleSelect}
            parserFunction={parse}
            nomarg
          />
          <HonestSelectInput
            key={`batch-fuel-type-${state.fuelType?.value || 'none'}`}
            name='fuelType'
            labelText='Fuel Type'
            onChange={handleSelect}
            options={fuelTypes}
            defaultValue={state.fuelType?.value}
            disabled={Boolean(state.fuelTank?.value)}
            nomarg
          />
          <CalendarInput
            name='date'
            labelText='Date & time'
            defaultValue={new Date(state.date)}
            onChange={handleDate}
            maxDate={new Date()}
            includeTime
            nomarg
          />
        </BatchFieldsGrid>
        <Flex width='100%'>
          <OtherText grey>DELIVERY VERIFICATION</OtherText>
        </Flex>
        <BatchFieldsGrid>
          <Input
            placeholder='Enter waybill ref'
            labelText='Waybill ref'
            value={state.waybillRef}
            onChange={handleChange}
            type='text'
            name='waybillRef'
            nomarg
          />
          <Input
            placeholder='Enter waybill litres'
            labelText='Waybill litres'
            value={state.waybillLitres}
            onChange={handleChange}
            type='number'
            name='waybillLitres'
            nomarg
          />
          <Input
            placeholder='Enter dip before'
            labelText='Tank dip before (L)'
            value={state.tankDipBefore}
            onChange={handleChange}
            type='number'
            name='tankDipBefore'
            nomarg
          />
          <Input
            placeholder='Enter dip after'
            labelText='Tank dip after (L)'
            value={state.tankDipAfter}
            onChange={handleChange}
            type='number'
            name='tankDipAfter'
            nomarg
          />
        </BatchFieldsGrid>
        <Input
          placeholder='e.g. S-4471, S-4472'
          labelText='Seal numbers'
          value={state.sealNumbers}
          onChange={handleChange}
          type='text'
          name='sealNumbers'
          nomarg
        />
        {measuredIn != null && (
          <Flex width='100%'>
            <OtherText
              color={
                variance == null || withinTolerance
                  ? tokens.status.success.fg
                  : tokens.status.danger.fg
              }
            >
              {`Measured in ${formatNumbersForDisplay(measuredIn)} L${
                variance != null
                  ? ` · variance ${formatNumbersForDisplay(variance)} L (${variancePct}%)${
                      withinTolerance
                        ? ' — within tolerance'
                        : ' — exceeds tolerance, claim will be raised'
                    }`
                  : ''
              }`}
            </OtherText>
          </Flex>
        )}
        <BatchModalFooter>
          <Button
            disabled={!state.amount || !state.price || !state.supplier || !state.fuelTank || !state.fuelType}
            orange
            onClick={handleSave}
          >
            Save
          </Button>
        </BatchModalFooter>
      </BatchModalBody>
    </Modal>
  )
}

export interface IFuelBatchModal {
  fuelTank: ISelectValue
  fuelType: ISelectValue
  amount: number
  price: number
  supplier: ISelectValue
  date: Date
  waybillRef: string
  waybillLitres: number
  tankDipBefore: number
  tankDipAfter: number
  sealNumbers: string
}
